// ═══════════════════════════════════════════════════════════════
// RollCall — Bulk Import Personnel from CSV
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, Progress,
} from '../../components/gab/GabComponents';
import { IconArrowLeft, IconDownload, IconUpload } from '@tabler/icons-react';
import Papa from 'papaparse';
import { notifications } from '../../lib/notifications';
import { useRollCallStore } from '../../store/rollcallStore';

interface ParsedRow {
  first_name?: string; last_name?: string; agency?: string;
  position?: string; pin?: string; badge_id?: string;
  phone?: string; email?: string;
  _idx: number; _errors: string[];
}

export default function PersonnelBulkImport() {
  const navigate = useNavigate();
  const { personnel, loadPersonnel, createPersonnel } = useRollCallStore();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ imported: number; failed: number; skipped: number } | null>(null);

  useEffect(() => { loadPersonnel(); }, []);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed: ParsedRow[] = (results.data as Record<string, string>[]).map((row, idx) => {
          const errs: string[] = [];
          if (!row.first_name?.trim()) errs.push('Missing first_name');
          if (!row.last_name?.trim()) errs.push('Missing last_name');
          if (!row.agency?.trim()) errs.push('Missing agency');
          if (row.pin && !/^\d{4,6}$/.test(row.pin)) errs.push('Invalid PIN');
          if (row.pin && personnel.some(p => p.pin === row.pin)) errs.push('Duplicate PIN');
          return { ...row, _idx: idx, _errors: errs };
        });
        // Check duplicates within file
        const pins: Record<string, boolean> = {};
        parsed.forEach(r => {
          if (r.pin) {
            if (pins[r.pin]) r._errors.push('Duplicate PIN in file');
            pins[r.pin] = true;
          }
        });
        setRows(parsed);
        setResult(null);
      },
    });
  };

  const validRows = rows.filter(r => r._errors.length === 0);
  const errorRows = rows.filter(r => r._errors.length > 0);

  const handleImport = async () => {
    setImporting(true);
    setProgress(0);
    let imported = 0;
    let failed = 0;
    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        await createPersonnel({
          firstName: row.first_name?.trim() || '',
          lastName: row.last_name?.trim() || '',
          agency: row.agency?.trim() || '',
          position: row.position?.trim() || '',
          pin: row.pin?.trim() || String(Math.floor(1000 + Math.random() * 9000)),
          badgeId: row.badge_id?.trim() || '',
          phone: row.phone?.trim() || '',
          email: row.email?.trim() || '',
          notes: '',
        });
        imported++;
      } catch {
        failed++;
      }
      setProgress(Math.round(((i + 1) / validRows.length) * 100));
    }
    setResult({ imported, failed, skipped: errorRows.length });
    setImporting(false);
    if (imported > 0) notifications.show({ message: `${imported} personnel imported`, color: 'var(--color-success)' });
    if (failed > 0) notifications.show({ message: `${failed} failed to import`, color: '#C68700' });
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse([
      { first_name: 'Jane', last_name: 'Doe', agency: 'City Fire Dept', position: 'responder', pin: '1234', badge_id: '', phone: '555-0100', email: 'jane@example.com' },
      { first_name: 'John', last_name: 'Smith', agency: 'County EMS', position: 'medical', pin: '5678', badge_id: 'EMS-001', phone: '555-0200', email: '' },
    ]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rollcall_personnel_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Bulk Import</Title>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rollcall/personnel')}>Back</Button>
      </Group>

      <Paper p="lg" radius="sm" withBorder maw={800}>
        <Group mb="md">
          <Button variant="light" size="xs" leftSection={<IconDownload size={14} />} onClick={downloadTemplate}>
            Download Template
          </Button>
        </Group>

        <div onClick={() => fileRef.current?.click()} style={{
          border: '2px dashed var(--frost)', borderRadius: 4, padding: '40px 24px',
          textAlign: 'center', cursor: 'pointer', marginBottom: 24,
        }}>
          <IconUpload size={32} style={{ color: 'var(--color-primary-700)', opacity: 0.5, marginBottom: 8 }} />
          <Text size="sm" fw={600}>Click to upload CSV file</Text>
          <Text size="xs" c="dimmed">Required columns: first_name, last_name, agency</Text>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
        </div>

        {rows.length > 0 && !result && (
          <>
            <Group mb="sm">
              <Badge color="var(--color-success)" size="lg">{validRows.length} valid</Badge>
              <Badge color="var(--color-error)" size="lg">{errorRows.length} errors</Badge>
              <Badge color="var(--color-primary-700)" size="lg">{rows.length} total</Badge>
            </Group>

            <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Agency</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Position</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>PIN</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)', background: r._errors.length ? 'var(--background-error-light)' : undefined }}>
                      <td style={{ padding: '8px 8px' }}>{i + 1}</td>
                      <td style={{ padding: '8px 8px' }}>{r.first_name} {r.last_name}</td>
                      <td style={{ padding: '8px 8px' }}>{r.agency || '—'}</td>
                      <td style={{ padding: '8px 8px' }}>{r.position || '—'}</td>
                      <td style={{ padding: '8px 8px', fontFamily: 'monospace' }}>{r.pin || 'auto'}</td>
                      <td style={{ padding: '8px 8px' }}>
                        {r._errors.length ? (
                          <Text size="xs" c="var(--color-error)">{r._errors.join(', ')}</Text>
                        ) : (
                          <Badge color="var(--color-success)" size="xs">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {importing && <Progress value={progress} mb="md" animated />}

            <Group justify="flex-end">
              <Button variant="light" onClick={() => { setRows([]); setResult(null); }}>Clear</Button>
              <Button onClick={handleImport} loading={importing} disabled={validRows.length === 0}
                leftSection={<IconUpload size={14} />}>Import {validRows.length} Personnel</Button>
            </Group>
          </>
        )}

        {result && (
          <Paper p="md" radius="sm" withBorder style={{ background: 'rgba(3,119,48,0.08)' }}>
            <Text fw={700} mb="xs">Import Complete</Text>
            <Text size="sm">{result.imported} imported, {result.failed} failed, {result.skipped} skipped</Text>
            <Group mt="md">
              <Button variant="light" onClick={() => { setRows([]); setResult(null); }}>Import More</Button>
              <Button onClick={() => navigate('/rollcall/personnel')}>View Roster</Button>
            </Group>
          </Paper>
        )}
      </Paper>
    </div>
  );
}
